import { Link, useLocation } from 'react-router-dom';
import { User, Settings, FolderKanban, LayoutDashboard, Store, ShieldCheck, BarChart, ClipboardList, Megaphone, MessageCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';

const BottomNav = () => {
  const location = useLocation();
  const { userData } = useAuth();
  const { t } = useLanguage();
  const [advertisingEnabled, setAdvertisingEnabled] = useState(true);

  useEffect(() => {
    const checkAdvertisingStatus = async () => {
      try {
        const systemSettingsDoc = await getDoc(doc(db, 'systemParams', 'systemSettings'));
        if (systemSettingsDoc.exists()) {
          const settings = systemSettingsDoc.data();
          setAdvertisingEnabled(settings.advertisingEnabled !== false);
        }
      } catch (error) {
        console.warn('Could not fetch advertising status:', error);
        setAdvertisingEnabled(true);
      }
    };

    checkAdvertisingStatus();
  }, []);
  
  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };
  
  const activeClass = "text-primary-600";
  const inactiveClass = "text-gray-600";
  
  // Show different navigation for admin
  if (userData?.role === 'admin') {
    return (
      <nav className="fixed bottom-0 left-0 right-0 bg-white shadow-lg border-t border-gray-200 z-40">
        <div className="container mx-auto px-4">
          <div className="flex justify-around items-center h-16">
            <Link 
              to="/admin/dashboard" 
              className={`flex flex-col items-center ${isActive('/admin/dashboard') ? activeClass : inactiveClass}`}
            >
              <LayoutDashboard className="h-6 w-6" />
              <span className="text-xs mt-1">{t('nav.dashboard')}</span>
            </Link>
            
            <Link 
              to="/admin/users" 
              className={`flex flex-col items-center ${isActive('/admin/users') ? activeClass : inactiveClass}`}
            >
              <User className="h-6 w-6" />
              <span className="text-xs mt-1">{t('admin.users')}</span>
            </Link>
            
            <Link 
              to="/admin/advertising" 
              className={`flex flex-col items-center ${isActive('/admin/advertising') ? activeClass : inactiveClass}`}
            >
              <Megaphone className="h-6 w-6" />
              <span className="text-xs mt-1">{t('advertising.title')}</span>
            </Link>
            
            <Link 
              to="/admin/support" 
              className={`flex flex-col items-center ${isActive('/admin/support') ? activeClass : inactiveClass}`}
            >
              <MessageCircle className="h-6 w-6" />
              <span className="text-xs mt-1">Support</span>
            </Link>
            
            <Link 
              to="/admin/profile" 
              className={`flex flex-col items-center ${isActive('/admin/profile') ? activeClass : inactiveClass}`}
            >
              <ShieldCheck className="h-6 w-6" />
              <span className="text-xs mt-1">Admin</span>
            </Link>
          </div>
        </div>
      </nav>
    );
  }

  // Regular navigation for other users
  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white shadow-lg border-t border-gray-200 z-40">
      <div className="container mx-auto px-4">
        <div className="flex justify-around items-center h-16">
          <Link 
            to={userData?.role === 'requester' ? '/requester/profile' : '/provider/profile'} 
            className={`flex flex-col items-center ${isActive('/requester/profile') || isActive('/provider/profile') ? activeClass : inactiveClass}`}
          >
            <User className="h-6 w-6" />
            <span className="text-xs mt-1">{t('nav.profile')}</span>
          </Link>

          {userData?.role === 'provider' && (
            <>
              <Link 
                to="/marketplace" 
                className={`flex flex-col items-center ${isActive('/marketplace') ? activeClass : inactiveClass}`}
              >
                <Store className="h-6 w-6" />
                <span className="text-xs mt-1">{t('nav.marketplace')}</span>
              </Link>

              {advertisingEnabled && (
                <Link 
                  to="/advertising" 
                  className={`flex flex-col items-center ${isActive('/advertising') ? activeClass : inactiveClass}`}
                >
                  <Megaphone className="h-6 w-6" />
                  <span className="text-xs mt-1">{t('advertising.title')}</span>
                </Link>
              )}

              <Link 
                to="/provider/submissions" 
                className={`flex flex-col items-center ${isActive('/provider/submissions') ? activeClass : inactiveClass}`}
              >
                <ClipboardList className="h-6 w-6" />
                <span className="text-xs mt-1">{t('submissions.mySubmissions')}</span>
              </Link>
            </>
          )}
          
          <Link 
            to={userData?.role === 'requester' ? '/requester/dashboard' : '/provider/dashboard'} 
            className={`flex flex-col items-center ${isActive('/requester/dashboard') || isActive('/provider/dashboard') ? activeClass : inactiveClass}`}
          >
            <LayoutDashboard className="h-6 w-6" />
            <span className="text-xs mt-1">{t('nav.dashboard')}</span>
          </Link>
        </div>
      </div>
    </nav>
  );
};

export default BottomNav;